import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, BarChart, Bar, Legend } from "recharts";
import { FileDown, FileText, PiggyBank, Gauge, Layers, Landmark, LineChart as LineIcon } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState, GainBadge, StatCard, Segmented } from "../components/ui";
import TimeFilterBar from "../components/TimeFilterBar";
import { useFilter } from "../context/FilterContext";
import { api, downloadExport } from "../lib/api";
import { pct, gainColor, shortDate, num } from "../lib/format";
import { timeAxis } from "../lib/chartAxis";
import { useMoney } from "../context/MoneyContext";
import { useTranslation } from "react-i18next";

const COLORS = ["#0052FF", "#3377FF", "#6699FF", "#99BBFF", "#CCDDFF", "#1E40AF", "#60A5FA", "#93C5FD"];
const PERIODS = ["1m", "1y", "5y", "all"];

export default function Analytics() {
  const { t } = useTranslation();
  const { dates } = useFilter();
  const { display } = useMoney();
  const [summary, setSummary] = useState(null);
  const [dividends, setDividends] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [risk, setRisk] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [divYears, setDivYears] = useState(null);
  const [perfPeriod, setPerfPeriod] = useState("1y");
  const [benchSymbol, setBenchSymbol] = useState("sp500");
  const [loading, setLoading] = useState(true);

  const load = useCallback((silent) => {
    if (!silent) setLoading(true);
    const p = {}; if (dates.start) p.start = dates.start; if (dates.end) p.end = dates.end;
    Promise.all([api.analyticsSummary(), api.dividends(p), api.allocation(), api.dividendsByYear()])
      .then(([s, d, a, dy]) => { setSummary(s.data); setDividends(d.data); setAllocation(a.data); setDivYears(dy.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [dates.start, dates.end]);
  useEffect(() => { load(); }, [load]);
  useRefreshOnReturn(() => load(true));

  useEffect(() => {
    api.risk(perfPeriod).then((r) => setRisk(r.data)).catch(() => setRisk(null));
    api.benchmark(perfPeriod, benchSymbol).then((r) => setBenchmark(r.data)).catch(() => setBenchmark(null));
  }, [perfPeriod, benchSymbol]);

  // 1M comes back as hourly points; the axis then needs a tick per day, times in the tooltip.
  const benchAxis = timeAxis(benchmark?.series, benchmark?.intraday, { dailyTick: shortDate });

  const doExport = async (fmt) => {
    try { await downloadExport("realized", fmt); toast.success(`Exported ${fmt.toUpperCase()}`); }
    catch { toast.error("Export failed"); }
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><div className="grid md:grid-cols-3 gap-6">{[0,1,2].map(i=><Skeleton key={i} className="h-36"/>)}</div></div>;

  const hasData = summary && summary.holdings_count > 0;
  const pieData = summary ? summary.instruments.map((i) => ({ name: i.ticker, value: i.market_value_usd })) : [];

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("analytics.subtitle")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("analytics.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-realized-csv" className="btn-outline" onClick={() => doExport("csv")}><FileDown size={16} /> Report CSV</button>
          <button data-testid="export-realized-pdf" className="btn-outline" onClick={() => doExport("pdf")}><FileText size={16} /> Report PDF</button>
        </div>
      </div>

      {!hasData ? (
        <EmptyState testid="analytics-empty" title={t("analytics.emptyTitle")} description={t("analytics.emptyDesc")} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard testid="an-total-return" label={t("analytics.totalReturn")} value={display(summary.total_return_usd).primary} secondary={display(summary.total_return_usd).secondary} sub={t("analytics.totalReturnSub")} subColor={gainColor(summary.total_return_usd)} />
            <StatCard testid="an-unrealized" label={t("analytics.unrealizedPL")} value={display(summary.unrealized_gain_usd).primary} secondary={display(summary.unrealized_gain_usd).secondary} sub={pct(summary.unrealized_gain_pct)} subColor={gainColor(summary.unrealized_gain_usd)} delay={0.05} />
            <StatCard testid="an-realized" label={t("analytics.realizedPL")} value={display(summary.realized_gain_usd).primary} secondary={display(summary.realized_gain_usd).secondary} sub={t("analytics.closedPositions")} subColor={gainColor(summary.realized_gain_usd)} delay={0.1} />
            <StatCard testid="an-dividends" label={t("analytics.dividendIncome")} value={display(dividends?.total_income_usd || 0).primary} secondary={display(dividends?.total_income_usd || 0).secondary} sub={t("analytics.inRange")} delay={0.15} />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Allocation */}
            <Panel className="card p-6 min-w-0" testid="allocation-panel">
              <h3 className="font-heading font-semibold tracking-tight text-lg mb-4">{t("analytics.allocation")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {pieData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => display(v).primary} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 space-y-2">
                {summary.instruments.slice(0, 5).map((i, idx) => (
                  <div key={i.ticker} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />{i.ticker}</span>
                    <span className="tabular text-slate-500">{i.allocation_pct}%</span>
                  </div>
                ))}
              </div>
            </Panel>

            {/* Per-instrument aggregate */}
            <Panel delay={0.05} className="card p-6 lg:col-span-2 min-w-0" testid="per-instrument-panel">
              <h3 className="font-heading font-semibold tracking-tight text-lg mb-4">{t("analytics.perInstrument")}</h3>
              <div className="overflow-x-auto">
              <div className="min-w-[480px]">
              <div className="grid grid-cols-12 gap-2 label pb-3 border-b border-slate-100">
                <div className="col-span-4">{t("holdings.instrument")}</div>
                <div className="col-span-3 text-right">{t("common.value")}</div>
                <div className="col-span-3 text-right">{t("analytics.gainUsd")}</div>
                <div className="col-span-2 text-right">%</div>
              </div>
              {summary.instruments.map((i) => (
                <Link key={i.ticker} to={`/instrument/${i.ticker}`} data-testid={`an-inst-${i.ticker}`} className="grid grid-cols-12 gap-2 py-3 border-b border-slate-50 last:border-0 items-center hover:bg-slate-50/60 rounded-lg px-1 transition-colors">
                  <div className="col-span-4">
                    <div className="font-medium">{i.ticker}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[160px]">{i.name}</div>
                  </div>
                  <div className="col-span-3 text-right tabular">{display(i.market_value_usd).primary}</div>
                  <div className={`col-span-3 text-right tabular ${gainColor(i.gain_usd)}`}>{display(i.gain_usd).primary}</div>
                  <div className="col-span-2 flex justify-end"><GainBadge value={i.gain_pct} /></div>
                </Link>
              ))}
              </div>
              </div>
            </Panel>
          </div>

          {/* Performance & Risk */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <h2 className="text-2xl font-heading font-semibold tracking-tight">{t("analytics.performanceRisk")}</h2>
            <Segmented options={PERIODS.map((p) => ({ value: p, label: t(`time.${p.toUpperCase()}`) }))} value={perfPeriod} onChange={setPerfPeriod} testid="perf-period" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testid="risk-vol" label={t("analytics.volatility")} icon={Gauge} value={risk?.volatility_pct != null ? `${risk.volatility_pct}%` : "—"} sub={t("analytics.volatilitySub")} />
            <StatCard testid="risk-dd" label={t("analytics.maxDrawdown")} icon={LineIcon} value={risk?.max_drawdown_pct != null ? `${risk.max_drawdown_pct}%` : "—"} subColor="text-rose-600" sub={t("analytics.maxDrawdownSub")} delay={0.05} />
            <StatCard testid="risk-sharpe" label={t("analytics.sharpe")} icon={Gauge} value={risk?.sharpe != null ? num(risk.sharpe) : "—"} sub={t("analytics.sharpeSub")} delay={0.1} />
            <StatCard testid="risk-days" label={t("analytics.bestWorst")} value={risk?.best_day_pct != null ? `${risk.best_day_pct}%` : "—"} sub={risk?.worst_day_pct != null ? t("analytics.worst", { value: risk.worst_day_pct }) : ""} subColor="text-rose-600" delay={0.15} />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <Panel className="card p-6 lg:col-span-2 min-w-0" testid="benchmark-panel">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2"><LineIcon size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("analytics.portfolioVs", { benchmark: benchmark?.benchmark_symbol || "S&P 500" })}</h3></div>
                <select data-testid="benchmark-select" value={benchSymbol} onChange={(e) => setBenchSymbol(e.target.value)} className="input py-1.5 w-auto text-sm font-medium">
                  {(benchmark?.options || [{ value: "sp500", label: "S&P 500" }, { value: "nasdaq", label: "Nasdaq" }, { value: "dax", label: "DAX" }, { value: "bitcoin", label: "Bitcoin" }, { value: "gold", label: "Gold" }]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {!benchmark || benchmark.series.length === 0 ? (
                <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.notEnoughHistory")}</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={benchmark.series} margin={{ top: 6, right: 6, left: -10, bottom: 0 }}>
                    <XAxis dataKey="date" {...benchAxis.axis} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={50} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${v}%`} />
                    <Tooltip formatter={(v, n) => [v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`, n]} {...benchAxis.tooltip} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="linear" dataKey="portfolio" name={t("analytics.yourPortfolio")} stroke="#0052FF" strokeWidth={2} dot={false} isAnimationActive={false} />
                    <Line type="linear" dataKey="benchmark" name={benchmark.benchmark_symbol} connectNulls stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Panel>

            <Panel delay={0.05} className="card p-6 min-w-0" testid="sector-allocation-panel">
              <div className="flex items-center gap-2 mb-4"><Layers size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("analytics.bySector")}</h3></div>
              {allocation && allocation.by_sector.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={allocation.by_sector} dataKey="value_usd" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                        {allocation.by_sector.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => display(v).primary} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3 space-y-1.5">
                    {allocation.by_sector.slice(0, 6).map((s, i) => (
                      <div key={s.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name}</span>
                        <span className="tabular text-slate-500">{s.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.noHoldings")}</div>}
            </Panel>
          </div>

          {/* Per-broker / per-account breakdown — how much is held at each bank */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel delay={0.05} className="card p-6 min-w-0" testid="broker-allocation-panel">
              <div className="flex items-center gap-2 mb-4"><Landmark size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("analytics.byBroker")}</h3></div>
              {allocation && allocation.by_broker.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={allocation.by_broker} dataKey="value_usd" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                        {allocation.by_broker.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => display(v).primary} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3 space-y-1.5">
                    {allocation.by_broker.map((b, i) => (
                      <div key={b.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{b.name}</span>
                        <span className="tabular text-slate-500">{display(b.value_usd).primary} · {b.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.noHoldings")}</div>}
            </Panel>

            <Panel delay={0.1} className="card p-6 min-w-0" testid="assettype-allocation-panel">
              <div className="flex items-center gap-2 mb-4"><Layers size={18} className="text-brand-500" /><h3 className="font-heading font-semibold tracking-tight text-lg">{t("analytics.byAssetType")}</h3></div>
              {allocation && allocation.by_asset_type.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={allocation.by_asset_type} dataKey="value_usd" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                        {allocation.by_asset_type.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => display(v).primary} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-3 space-y-1.5">
                    {allocation.by_asset_type.map((s, i) => (
                      <div key={s.name} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{s.name}</span>
                        <span className="tabular text-slate-500">{display(s.value_usd).primary} · {s.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.noHoldings")}</div>}
            </Panel>
          </div>

          {/* Dividends */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-2xl font-heading font-semibold tracking-tight">{t("analytics.dividendIncomeTitle")}</h2>
            <TimeFilterBar />
          </div>
          {divYears && divYears.years.length > 0 && (
            <Panel className="card p-6 min-w-0" testid="dividends-by-year">
              <h3 className="font-heading font-semibold tracking-tight text-lg mb-4">{t("analytics.dividendByYear")}</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={divYears.years} margin={{ top: 6, right: 6, left: -6, bottom: 0 }}>
                  <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => display(v).primary} />
                  <Tooltip formatter={(v) => [display(v).primary, "Dividends"]} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Bar dataKey="income_usd" fill="#0052FF" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          )}
          {(!dividends || dividends.instruments.length === 0) ? (
            <EmptyState testid="dividends-empty" title={t("analytics.dividendsEmptyTitle")} description={t("analytics.dividendsEmptyDesc")} image="/images/empty-dividends.jpg" />
          ) : (
            <div className="grid md:grid-cols-2 gap-5" data-testid="dividends-list">
              {dividends.instruments.map((d, idx) => (
                <Panel key={d.ticker} delay={idx * 0.04} className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <PiggyBank size={18} className="text-brand-500" />
                      <span className="font-heading font-semibold tracking-tight">{d.ticker}</span>
                    </div>
                    <span className="tabular font-semibold text-emerald-600">{display(d.total_income_usd).primary}</span>
                  </div>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto">
                    {d.payouts.map((p, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">{shortDate(p.date)}</span>
                        <span className="text-slate-400 tabular">{num(p.shares_held, 2)} {t("analytics.shares")} × {p.amount_per_share}</span>
                        <span className="tabular text-slate-700">{display(p.income_usd).primary}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
